import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Tooltip,
  Table,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { useForm } from "antd/es/form/Form";
import { useState } from "react";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ModalHeader, modalButtonsTheme } from "components/modals/ModalChrome";
import { showConfirmModal } from "components/modals/confirmModal";
import RedeemDiscountVoucherModal from "components/modals/RedeemDiscountVoucherModal.component";
import {
  useRedeemVoucherCredit,
  useVoidClaimedVoucher,
  useVoidVoucher,
} from "hooks/mutations/loyalty";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import { translate, translateWithVars } from "shared/languages";
import { LoyaltyRewardType, VoucherStatus } from "shared/types/enum";
import { AdminCapability } from "shared/utils/capabilities";
import { CustomerLoyaltyVoucher } from "shared/types/loyalty";
import {
  VOUCHER_STATUS_LABELS,
  VOUCHER_STATUS_TONE,
  VoucherEffectiveStatus,
} from "shared/utils";
import { rewardLabel, voucherReason } from "shared/utils/loyalty";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// Voucher status labels + tones live in shared/utils (VOUCHER_STATUS_*).
const voucherTone = (status: string) =>
  VOUCHER_STATUS_TONE[status as VoucherEffectiveStatus] ?? "neutral";
const voucherLabel = (status: string) =>
  VOUCHER_STATUS_LABELS[status as VoucherEffectiveStatus] ?? status;

// The voucher list + its whole action surface (claim credit, use discount,
// void, claw back a claimed credit), extracted from LoyaltyTab so the Reward
// tab's Referral pane renders referral-sourced vouchers through the SAME
// machinery — every endpoint is voucher-id-based and source-agnostic.
const VoucherSection = ({
  vouchers,
  isDeactivated,
  title,
  subtitle,
  emptyText,
  customerId,
}: {
  vouchers: CustomerLoyaltyVoucher[];
  /** The client these vouchers belong to (the discount pop-up reads their
   *  pending referral). */
  customerId: string;
  // Redeeming for a deactivated client is blocked (mirrors the buy button).
  isDeactivated: boolean;
  title: string;
  subtitle?: string;
  emptyText: string;
}) => {
  const redeemCredit = useRedeemVoucherCredit();
  const voidVoucher = useVoidVoucher();
  const voidClaimed = useVoidClaimedVoucher();
  // Pembatasan Admin (System Review R1): claiming a credit hands out a free
  // class, so it follows "Ubah Kredit"; voiding a voucher or clawing a credit
  // back takes a reward away, so both follow "Hapus / Masa Berlaku Paket".
  // Using a discount voucher is a sale and stays open. Plainly disabled, no
  // hover reason (house rule); the server refuses too.
  const { isRestricted } = useAdminCapabilities();
  const claimRestricted = isRestricted(AdminCapability.CreditAdjustment);
  const voidRestricted = isRestricted(AdminCapability.PackageVoidExpiry);
  // Referral vouchers were never earned by stamps, so their void pop-ups
  // carry no stamp-card sentence (R13).
  const isReferral = (v: CustomerLoyaltyVoucher) =>
    v.source === "referral_referrer" || v.source === "referral_friend";

  const [voidTarget, setVoidTarget] = useState<CustomerLoyaltyVoucher | null>(
    null
  );
  const [voidForm] = useForm();
  const [clawbackTarget, setClawbackTarget] =
    useState<CustomerLoyaltyVoucher | null>(null);
  const [clawbackForm] = useForm();
  const [discountTarget, setDiscountTarget] =
    useState<CustomerLoyaltyVoucher | null>(null);

  const onVoidSubmit = async () => {
    const v = await voidForm.validateFields().catch(() => null);
    if (!v || !voidTarget) return;
    voidVoucher.mutate(
      { voucherId: voidTarget.id, reason: v.reason },
      {
        onSuccess: () => {
          setVoidTarget(null);
          voidForm.resetFields();
        },
      }
    );
  };

  const onClawbackSubmit = async () => {
    const v = await clawbackForm.validateFields().catch(() => null);
    if (!v || !clawbackTarget) return;
    voidClaimed.mutate(
      { voucherId: clawbackTarget.id, reason: v.reason },
      {
        onSuccess: () => {
          setClawbackTarget(null);
          clawbackForm.resetFields();
        },
      }
    );
  };

  const confirmClaimCredit = (voucher: CustomerLoyaltyVoucher) => {
    showConfirmModal({
      title: translate("loyalty.claim.title"),
      content: (
        <Flex vertical gap={4}>
          <Text strong>{rewardLabel(voucher)}</Text>
          <Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("loyalty.claim.body")}
          </Text>
          <Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("loyalty.claim.deadline")}{" "}
            <strong>
              {formatDate(voucher.expires_at, DEFAULT_DATE_FORMAT)}
            </strong>
          </Text>
        </Flex>
      ),
      confirmLabel: translate("loyalty.claim.confirm"),
      onConfirm: () => redeemCredit.mutate(voucher.id),
    });
  };

  const voucherColumns: TableProps<CustomerLoyaltyVoucher>["columns"] = [
    {
      title: th(translate("loyalty.col.reward")),
      key: "reward",
      render: (_, v) => (
        <span style={{ fontWeight: 600 }}>{rewardLabel(v)}</span>
      ),
    },
    {
      title: th(translate("loyalty.col.earned_at")),
      dataIndex: "issued_at",
      key: "issued_at",
      render: (d: string) => (
        <span style={tnum}>{formatDate(d, DEFAULT_DATE_FORMAT)}</span>
      ),
      sorter: (a, b) =>
        new Date(a.issued_at).getTime() - new Date(b.issued_at).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: th(translate("loyalty.col.valid_until")),
      dataIndex: "expires_at",
      key: "expires_at",
      render: (d: string) => (
        <span style={tnum}>{formatDate(d, DEFAULT_DATE_FORMAT)}</span>
      ),
    },
    {
      title: th(translate("label.status")),
      key: "status",
      render: (_, v) => (
        <StatusBadge tone={voucherTone(v.status)} label={voucherLabel(v.status)} />
      ),
    },
    {
      title: th(translate("loyalty.col.reason")),
      key: "reason",
      render: (_: unknown, row: CustomerLoyaltyVoucher) => {
        const r = voucherReason(row);
        if (!r.text) return <Text type="secondary">-</Text>;
        return (
          <Flex vertical>
            <Text
              style={{ fontSize: textPx(13) }}
              type={r.system ? "secondary" : undefined}
            >
              {r.text}
            </Text>
            {r.actor && (
              <Text type="secondary" style={{ fontSize: textPx(12) }}>
                {r.actor}
              </Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: th(translate("label.action")),
      key: "action",
      render: (_, v) => {
        // A CLAIMED free credit can be clawed back while it is untouched (and
        // for loyalty vouchers, while the client has earned no newer stamp).
        // The server computes that as claim_revocable (and re-checks on
        // submit); a disabled button with the reason beats a tappable one
        // that 422s.
        if (v.status === VoucherStatus.Redeemed) {
          if (v.type !== LoyaltyRewardType.FreeCredit) return null;
          // A deactivated client is read-only here: plainly disabled, no
          // hover reason (the client page's banner says why).
          const revoke = (
            <Button
              size="small"
              danger
              disabled={isDeactivated || voidRestricted || !v.claim_revocable}
              onClick={() => setClawbackTarget(v)}
            >
              {translate("loyalty.action.void_claimed")}
            </Button>
          );
          if (v.claim_revocable || isDeactivated || voidRestricted) return revoke;
          // Blocked by the next-stamp rule: name the card that holds the
          // blocking stamp (R22). A stamp on a finished card can't be removed
          // to unblock this, and the hint says so instead of leaving the
          // admin to try Remove 1 stamp.
          const hint = v.claim_blocked_by
            ? v.claim_blocked_by.on_current_card
              ? translate("loyalty.action.void_claimed_hint_current_card")
              : translateWithVars("loyalty.action.void_claimed_hint_closed_card", {
                  date: formatDate(v.claim_blocked_by.card_started_at, DEFAULT_DATE_FORMAT),
                })
            : translate("loyalty.action.void_claimed_hint");
          return <Tooltip title={hint}>{revoke}</Tooltip>;
        }
        if (v.status !== VoucherStatus.Issued) return null;
        const redeem =
          v.type === LoyaltyRewardType.FreeCredit ? (
            <Button
              size="small"
              type="primary"
              disabled={isDeactivated || claimRestricted}
              onClick={() => confirmClaimCredit(v)}
            >
              {translate("loyalty.action.claim_credit")}
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              disabled={isDeactivated}
              onClick={() => setDiscountTarget(v)}
            >
              {translate("loyalty.action.use_discount")}
            </Button>
          );
        return (
          <Flex gap={8}>
            {redeem}
            <Button
              size="small"
              danger
              disabled={isDeactivated || voidRestricted}
              onClick={() => setVoidTarget(v)}
            >
              {translate("loyalty.action.void")}
            </Button>
          </Flex>
        );
      },
    },
  ];

  return (
    <>
      <Card>
        <Flex vertical gap={18}>
          <CardSectionTitle title={title} subtitle={subtitle} />
          <Table
            showSorterTooltip={false}
            rowKey="id"
            columns={voucherColumns}
            dataSource={vouchers}
            pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
            locale={{ emptyText }}
          />
        </Flex>
      </Card>

      {/* ---- Void voucher modal ---- */}
      <ConfigProvider theme={modalButtonsTheme}>
        <Modal
          open={!!voidTarget}
          onOk={onVoidSubmit}
          onCancel={() => setVoidTarget(null)}
          okText={translate("loyalty.action.void")}
          cancelText={translate("button.cancel")}
          okButtonProps={{ danger: true }}
          confirmLoading={voidVoucher.isPending}
          afterClose={() => voidForm.resetFields()}
          title={
            <ModalHeader
              eyebrow={voidTarget ? rewardLabel(voidTarget) : undefined}
              title={translate("loyalty.void.title")}
              tone="error"
            />
          }
        >
          <Flex vertical gap={10}>
            <Text type="secondary">
              {translate(
                voidTarget && isReferral(voidTarget)
                  ? "loyalty.void.body_no_card"
                  : "loyalty.void.body"
              )}
            </Text>
            <Form form={voidForm} layout="vertical">
              <Form.Item
                name="reason"
                label={translate("loyalty.adjust.reason")}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: translate("loyalty.adjust.reason_required"),
                  },
                ]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </Flex>
        </Modal>
      </ConfigProvider>

      {/* ---- Claw back a claimed credit ---- */}
      <ConfigProvider theme={modalButtonsTheme}>
        <Modal
          open={!!clawbackTarget}
          onOk={onClawbackSubmit}
          onCancel={() => setClawbackTarget(null)}
          okText={translate("loyalty.action.void_claimed")}
          cancelText={translate("button.cancel")}
          okButtonProps={{ danger: true }}
          confirmLoading={voidClaimed.isPending}
          afterClose={() => clawbackForm.resetFields()}
          title={
            <ModalHeader
              eyebrow={clawbackTarget ? rewardLabel(clawbackTarget) : undefined}
              title={translate("loyalty.void_claimed.title")}
              tone="error"
            />
          }
        >
          <Flex vertical gap={10}>
            <Text type="secondary">
              {translate(
                clawbackTarget && isReferral(clawbackTarget)
                  ? "loyalty.void_claimed.body_no_card"
                  : "loyalty.void_claimed.body"
              )}
            </Text>
            <Form form={clawbackForm} layout="vertical">
              <Form.Item
                name="reason"
                label={translate("loyalty.adjust.reason")}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: translate("loyalty.adjust.reason_required"),
                  },
                ]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </Flex>
        </Modal>
      </ConfigProvider>

      {/* ---- Discount redemption ---- */}
      <ConfigProvider theme={modalButtonsTheme}>
        <RedeemDiscountVoucherModal
          voucher={discountTarget}
          open={!!discountTarget}
          onCancel={() => setDiscountTarget(null)}
          customerId={customerId}
        />
      </ConfigProvider>
    </>
  );
};

export default VoucherSection;
